import { createFileRoute, Link } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { BLOCK_GUIDES, blockPath } from "../lib/blocking";
import {
  breadcrumbJsonLd,
  canonical,
  collectionJsonLd,
  documentHeaders,
  socialMeta,
} from "../lib/seo";

const TITLE = "How to block any app on your iPhone — Bible Shield";
const DESCRIPTION =
  "One page per app, because they fail differently: what makes each one hard to close, the Screen Time steps, and what to do when the limit stops holding.";

/**
 * `/block` — the hub.
 *
 * ── Why one page per app ─────────────────────────────────────────────────────
 *
 * "How to block <app> on iPhone" is not one search wearing thirty costumes. The
 * Instagram version is about a second feed hidden behind the first; the Reddit
 * version is about a comment tree with no bottom; the Snapchat version is about
 * a number that resets if you stop. A single "how to block apps" page answers
 * none of those, which is why the sites that publish one rank for none of them.
 *
 * The steps are shared across every guide (`lib/blocking/steps.ts`) precisely so
 * they cannot be what distinguishes the pages. What distinguishes them is the
 * app's own design, which is the part a reader who has already tried Screen
 * Time twice actually needs.
 */
export const Route = createFileRoute("/block/")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/block",
        lang: "en",
      }),
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "Block an app", path: "/block" },
        ]),
      },
      {
        "script:ld+json": collectionJsonLd({
          name: TITLE,
          description: DESCRIPTION,
          path: "/block",
          items: BLOCK_GUIDES.map((g) => ({
            name: g.h1,
            path: blockPath(g.slug),
          })),
        }),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/block") }],
  }),
  component: BlockIndex,
});

function BlockIndex() {
  return (
    <PageChrome>
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        block an app
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        They do not all fail the same way
      </h1>
      <p className="mt-4 max-w-[60ch] text-lg leading-relaxed text-ink2">
        The Screen Time steps are the same for every app on this list. What is
        not the same is why the limit stops holding — one app has no seam to
        stop at, another keeps a number that resets if you leave, another hides
        a second feed behind the first. That is what each page is about.
      </p>

      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        {BLOCK_GUIDES.map((guide) => (
          <li key={guide.slug}>
            <Link
              to="/block/$slug"
              params={{ slug: guide.slug }}
              className="brutal flex h-full flex-col gap-3 p-6 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {guide.app}
              </span>
              <span className="font-display text-xl leading-snug font-bold text-ink">
                {guide.h1}
              </span>
              <span className="leading-relaxed text-body">
                {guide.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-14 border-t-[2.5px] border-ink pt-10">
        <h2 className="font-display text-2xl font-bold text-ink">
          One thing before you start
        </h2>
        <p className="mt-3 max-w-[60ch] leading-relaxed text-ink2">
          Bible Shield is iPhone and iPad only — the blocking is built on
          Apple's Screen Time and the Android build is not published yet.
          Everything these pages say about Android is Google's own Digital
          Wellbeing.
        </p>
      </section>
    </PageChrome>
  );
}
